"use client";

import React, { useCallback } from "react";
import { Mail, Copy, X } from "lucide-react";
import { toast } from "sonner";
import { ResponsiveModal, ModalContainer, MODAL_IDS } from "@wtc/ui";
import { Button } from "@wtc/ui/components/button";
import type { BulkEmailExportModalData } from "@/lib/modals/data/modal-data-types";

export function BulkEmailExportModal() {
  return (
    <ModalContainer<BulkEmailExportModalData> modalId={MODAL_IDS.importExport.bulkExportEmailsContacts}>
      {({ isOpen, data, onClose }) => (
        <BulkEmailExportModalContent
          isOpen={isOpen}
          data={data}
          onClose={onClose}
        />
      )}
    </ModalContainer>
  );
}

interface BulkEmailExportModalContentProps {
  isOpen: boolean;
  data?: BulkEmailExportModalData;
  onClose: () => void;
}

function BulkEmailExportModalContent({
  isOpen,
  data,
  onClose,
}: BulkEmailExportModalContentProps) {
  const emailList = data?.emailList ?? "";
  const totalCount = data?.totalCount ?? 0;
  const entityType = data?.entityType ?? "contacts";

  const handleCopyToClipboard = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(emailList);
      toast.success("Email list copied to clipboard");
    } catch (error) {
      toast.error("Failed to copy to clipboard");
      console.error("Copy to clipboard error:", error);
    }
  }, [emailList]);

  const getTitle = () => {
    if (entityType === "companies") {
      return "Company Contact Emails";
    }
    return "Contact Emails";
  };

  const getDescription = () => {
    if (entityType === "companies") {
      return `Found ${totalCount} unique email addresses from contacts across selected companies`;
    }
    return `Found ${totalCount} email addresses from selected contacts`;
  };

  return (
    <ResponsiveModal
      globalModalId={MODAL_IDS.importExport.bulkExportEmailsContacts}
      title={getTitle()}
      maxWidth="lg"
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-600 dark:text-gray-400">{getDescription()}</p>

        {/* Email list textarea */}
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Semicolon-separated email list:
          </label>
          <textarea
            readOnly
            value={emailList}
            className="w-full p-3 border border-gray-300 dark:border-gray-700 rounded-md bg-gray-50 dark:bg-gray-900 font-mono text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 max-h-[40vh]"
          />
        </div>

        {/* Action buttons */}
        <div className="flex gap-2 justify-end pt-4">
          <Button variant="outline" onClick={onClose} className="gap-2">
            <X className="h-4 w-4" />
            Close
          </Button>
          <Button onClick={handleCopyToClipboard} className="gap-2">
            <Copy className="h-4 w-4" />
            Copy to Clipboard
          </Button>
        </div>
      </div>
    </ResponsiveModal>
  );
}
